import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Spinner, Toast } from 'react-bootstrap';
import { FileIcon, defaultStyles } from 'react-file-icon';
import { downloadFile } from '../api/api';
import './FileGrid.css';

function FileGrid({ files, clientId, folder }) {

    const { projectId } = useParams();
  const [downloading, setDownloading] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({ type: '', message: '' });

  // Format date to be more readable
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    
    // Get relative time if less than 24 hours ago
    const hoursAgo = (new Date() - date) / (1000 * 60 * 60);
    if (hoursAgo < 24) {
      const hours = Math.floor(hoursAgo);
      const minutes = Math.floor((hoursAgo - hours) * 60);
      if (hours === 0) {
        return `${minutes} minutes ago`;
      }
      return `${hours} hours ago`;
    }
    
    // For older dates, show the full date in a readable format
    const options = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    };
    
    return date.toLocaleString('en-US', options);
  };

  // Format filename to show only text after underscore
  const formatFileName = (fileName) => {
    const parts = fileName.split('_');
    return parts.length > 1 ? parts[parts.length - 1] : fileName;
  };

  // Format file size to be more readable
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const handleDownload = async (file) => {
    setDownloading(prev => ({ ...prev, [file.name]: true }));
    try {
      await downloadFile(clientId, folder, file.name);
      setToastMessage({
        type: 'success',
        message: `Successfully started downloading ${file.name}`
      });
    } catch (error) {
      setToastMessage({
        type: 'error',
        message: `Failed to download ${file.name}: ${error.message}`
      });
    } finally {
      setDownloading(prev => ({ ...prev, [file.name]: false }));
      setShowToast(true);
    }
  };

  const renderPreview = (file) => {
    if (file.contentType.startsWith('image/')) {
      return (
        <div className="preview-container">
          <img 
            src={file.downloadUrl} 
            alt={file.name} 
            className="file-preview"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/icons/image-error.png';
            }}
          />
        </div>
      );
    } else {
      return (
        <div className="file-icon-container">
          <FileIcon 
            extension={file.extension} 
            {...defaultStyles[file.extension]} 
          />
        </div>
      );
    }
  };

  return (
    <>
      <div className="file-grid">
        {files.map((file) => (
          <div key={file.name} className="file-card">
            <div className="file-preview-wrapper">
              {renderPreview(file)}
            </div>
            <div className="file-info">
              <h6 className="file-name" title={file.name}>
                {formatFileName(file.name)}
              </h6>
              <p className="file-details">
                {formatFileSize(file.size)} • {formatDate(file.updateTime)}
              </p>
              <p className="file-type">{file.contentType}</p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleDownload(file)}
                disabled={downloading[file.name]}
                className="download-button"
              >
                {downloading[file.name] ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Downloading...
                  </>
                ) : (
                  'Download'
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Toast 
        show={showToast} 
        onClose={() => setShowToast(false)}
        delay={3000}
        autohide
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          minWidth: '250px'
        }}
      >
        <Toast.Header>
          <strong className="me-auto">
            {toastMessage.type === 'success' ? 'Success' : 'Error'}
          </strong>
        </Toast.Header>
        <Toast.Body>
          {toastMessage.message}
        </Toast.Body>
      </Toast>
    </>
  );
}

export default FileGrid;